
import React, { useState, useEffect } from "react";
import { saveProjectPeriods, getProjectPeriods } from "../api/projectPeriod-api";
import { getAllProjectsByUuid } from "../api/project-api";
import { ProjectPeriod } from "../model/projectPeriod";
import { FormProvider, useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSnackbar } from "react-simple-snackbar";
import moment from "moment";

type ProjectWithDates = {
    pid: string;
    start_dt: string;
    end_dt: string;
    [key: string]: any;
};

export default function BillingPeriodConfig(props: { profile: any; projects?: any[] }) {
    const [projectDates, setProjectDates] = useState<{ start_dt: string, end_dt: string } | null>(null);
    const [billingPeriods, setBillingPeriods] = useState<any[]>([]);
    const [newPeriodStart, setNewPeriodStart] = useState<Date | null>(null);
    const [newPeriodEnd, setNewPeriodEnd] = useState<Date | null>(null);
    const [editingPeriodId, setEditingPeriodId] = useState<string | null>(null);
    const [editStart, setEditStart] = useState<Date | null>(null);
    const [editEnd, setEditEnd] = useState<Date | null>(null);

    // Sub-period states
    const [expandedPeriodId, setExpandedPeriodId] = useState<string | null>(null);
    const [newSubPeriodStart, setNewSubPeriodStart] = useState<Date | null>(null);
    const [newSubPeriodEnd, setNewSubPeriodEnd] = useState<Date | null>(null);
    const [editingSubPeriodId, setEditingSubPeriodId] = useState<string | null>(null);
    const [editSubStart, setEditSubStart] = useState<Date | null>(null);
    const [editSubEnd, setEditSubEnd] = useState<Date | null>(null);

    const [openSnackbar] = useSnackbar();

    // Check if any project uses customized billing period
    const hasCustomizedProject = (props.projects || [])?.some(
        (project: any) => project.billing_period === "Customized"
    );

    useEffect(() => {
        // Load periods from DB
        if (!props.profile?.uuid || !props.profile?.pid) return;
        getProjectPeriods(props.profile.uuid, props.profile.pid).then((periods) => {
            // Only show periods where record_st_dt and record_end_dt are set and period_st_dt/period_end_dt are blank/null
            const mainPeriods = periods.filter(
                (p) => p.record_st_dt && p.record_end_dt && (!p.period_st_dt && !p.period_end_dt)
            );
            // For each main period, find its weeks (where period_st_dt/period_end_dt are set and match parent record_st_dt/record_end_dt)
            const grouped = mainPeriods.map((main) => {
                const sub_periods = periods
                    .filter(
                        (w) =>
                            w.record_st_dt === main.record_st_dt &&
                            w.record_end_dt === main.record_end_dt &&
                            w.period_st_dt && w.period_end_dt
                    )
                    .map((w) => ({
                        id: `${w.period_st_dt}_${w.period_end_dt}_w${w.index}`,
                        start_date: w.period_st_dt,
                        end_date: w.period_end_dt,
                        week_number: w.index,
                    }));
                return {
                    id: `${main.record_st_dt}_${main.record_end_dt}`,
                    start_date: main.record_st_dt,
                    end_date: main.record_end_dt,
                    sub_periods,
                };
            });
            setBillingPeriods(grouped);
        });

        // Fetch project start/end dates for this employee
        if (props.profile?.uuid) {
            getAllProjectsByUuid(props.profile.uuid).then((projects: ProjectWithDates[]) => {
                // Find the project matching the current pid, or just take the first if not found
                let project: ProjectWithDates | undefined = undefined;
                if (Array.isArray(projects)) {
                    project = projects.find((p) => p.pid === props.profile.pid) || projects[0];
                }
                if (project && project.start_dt && project.end_dt) {
                    setProjectDates({ start_dt: project.start_dt, end_dt: project.end_dt });
                } else {
                    setProjectDates(null);
                }
            });
        }
    }, [props.profile.uuid, props.profile.pid]);

    const addPeriod = async () => {

        if (!newPeriodStart || !newPeriodEnd) {
            openSnackbar("Please select both start and end dates");
            return;
        }

        if (newPeriodStart > newPeriodEnd) {
            openSnackbar("Start date must be before end date");
            return;
        }

        // Check if period is within project start/end
        if (projectDates) {
            const projectStart = moment(projectDates.start_dt, "YYYY-MM-DD").toDate();
            const projectEnd = moment(projectDates.end_dt, "YYYY-MM-DD").toDate();
            if (newPeriodStart < projectStart || newPeriodEnd > projectEnd) {
                openSnackbar("Period start/end must be within the project's start/end dates.");
                return;
            }
        }

        // Check for overlap with existing periods
        const isOverlap = billingPeriods.some(period => {
            const existingStart = moment(period.start_date, "YYYY-MM-DD").toDate();
            const existingEnd = moment(period.end_date, "YYYY-MM-DD").toDate();
            // If new period starts before existing ends and ends after existing starts, overlap
            return (
                newPeriodStart <= existingEnd && newPeriodEnd >= existingStart
            );
        });
        if (isOverlap) {
            openSnackbar("Cannot save: Dates overlap with an existing period.");
            return;
        }

        const newPeriod = {
            id: Date.now().toString(),
            start_date: moment(newPeriodStart).format("YYYY-MM-DD"),
            end_date: moment(newPeriodEnd).format("YYYY-MM-DD"),
            sub_periods: [],
            created_at: new Date().toISOString(),
        };

        const updated = [...billingPeriods, newPeriod];
        setBillingPeriods(updated);
        // Save only the new period (week_no=0)
        console.log("Saving new period to DB", {
            uuid: props.profile.uuid,
            pid: props.profile.pid,
            index: 0,
            record_st_dt: newPeriod.start_date,
            record_end_dt: newPeriod.end_date,
        });
        await saveProjectPeriods(props.profile.uuid, props.profile.pid, [
            {
                index: 0,
                record_st_dt: newPeriod.start_date,
                record_end_dt: newPeriod.end_date,
            },
        ]);

        setNewPeriodStart(null);
        setNewPeriodEnd(null);
        openSnackbar("Billing period added successfully");
    };

    const updatePeriod = async (id: string) => {
        if (!editStart || !editEnd) {
            openSnackbar("Please select both start and end dates");
            return;
        }

        if (editStart > editEnd) {
            openSnackbar("Start date must be before end date");
            return;
        }

        const updated = billingPeriods.map((period) =>
            period.id === id
                ? {
                      ...period,
                      start_date: moment(editStart).format("YYYY-MM-DD"),
                      end_date: moment(editEnd).format("YYYY-MM-DD"),
                  }
                : period
        );

        setBillingPeriods(updated);
        // Update DB: replace all periods for this project
        const dbPeriods = updated.flatMap((p) => [
            { week_no: 0, period_st_dt: p.start_date, period_end_dt: p.end_date },
            ...(p.sub_periods || []).map((s) => ({
                week_no: s.week_number,
                period_st_dt: s.start_date,
                period_end_dt: s.end_date,
            })),
        ]);
        console.log("Updating period(s) in DB", {
            uuid: props.profile.uuid,
            pid: props.profile.pid,
            dbPeriods
        });
        await saveProjectPeriods(props.profile.uuid, props.profile.pid, dbPeriods);
        setEditingPeriodId(null);
        openSnackbar("Billing period updated successfully");
    };

    const deletePeriod = async (id: string) => {
        const updated = billingPeriods.filter((period) => period.id !== id);
        setBillingPeriods(updated);
        // Update DB: replace all periods for this project
        const dbPeriods = updated.flatMap((p) => [
            { week_no: 0, period_st_dt: p.start_date, period_end_dt: p.end_date },
            ...(p.sub_periods || []).map((s) => ({
                week_no: s.week_number,
                period_st_dt: s.start_date,
                period_end_dt: s.end_date,
            })),
        ]);
        console.log("Deleting period(s) in DB", {
            uuid: props.profile.uuid,
            pid: props.profile.pid,
            dbPeriods
        });
        await saveProjectPeriods(props.profile.uuid, props.profile.pid, dbPeriods);
        openSnackbar("Billing period deleted successfully");
    };

    // Sub-period functions
    const addSubPeriod = async (periodId: string) => {
        if (!newSubPeriodStart || !newSubPeriodEnd) {
            openSnackbar("Please select both start and end dates for sub-period");
            return;
        }

        if (newSubPeriodStart > newSubPeriodEnd) {
            openSnackbar("Start date must be before end date");
            return;
        }

        // Check for overlap with existing weeks in this period
        const parentPeriod = billingPeriods.find(p => p.id === periodId);
        if (parentPeriod) {
            // Check week is within parent period
            const parentStart = moment(parentPeriod.start_date, "YYYY-MM-DD").toDate();
            const parentEnd = moment(parentPeriod.end_date, "YYYY-MM-DD").toDate();
            if (newSubPeriodStart < parentStart || newSubPeriodEnd > parentEnd) {
                openSnackbar("Week start/end must be within its period's start/end dates.");
                return;
            }
            if (parentPeriod.sub_periods) {
                const isWeekOverlap = parentPeriod.sub_periods.some(week => {
                    const weekStart = moment(week.start_date, "YYYY-MM-DD").toDate();
                    const weekEnd = moment(week.end_date, "YYYY-MM-DD").toDate();
                    return (
                        newSubPeriodStart <= weekEnd && newSubPeriodEnd >= weekStart
                    );
                });
                if (isWeekOverlap) {
                    openSnackbar("Cannot save: Week dates overlap with an existing week.");
                    return;
                }
            }
        }

        const updated = billingPeriods.map((period) => {
            if (period.id === periodId) {
                const subPeriod = {
                    id: Date.now().toString(),
                    start_date: moment(newSubPeriodStart).format("YYYY-MM-DD"),
                    end_date: moment(newSubPeriodEnd).format("YYYY-MM-DD"),
                    week_number: (period.sub_periods?.length || 0) + 1,
                };
                return {
                    ...period,
                    sub_periods: [...(period.sub_periods || []), subPeriod],
                };
            }
            return period;
        });

        setBillingPeriods(updated);
        // Save only the new week (week_no=N)
        const period = updated.find((p) => p.id === periodId);
        const newWeek = period.sub_periods[period.sub_periods.length - 1];
        // Ensure all dates are formatted as 'YYYY-MM-DD'
        const record_st_dt = moment(period.start_date).format("YYYY-MM-DD");
        const record_end_dt = moment(period.end_date).format("YYYY-MM-DD");
        const period_st_dt = moment(newWeek.start_date).format("YYYY-MM-DD");
        const period_end_dt = moment(newWeek.end_date).format("YYYY-MM-DD");
        console.log("Saving new week to DB", {
            uuid: props.profile.uuid,
            pid: props.profile.pid,
            index: newWeek.week_number,
            record_st_dt,
            record_end_dt,
            period_st_dt,
            period_end_dt,
        });
        await saveProjectPeriods(props.profile.uuid, props.profile.pid, [
            {
                index: newWeek.week_number,
                record_st_dt,
                record_end_dt,
                period_st_dt,
                period_end_dt,
            },
        ]);
        setNewSubPeriodStart(null);
        setNewSubPeriodEnd(null);
        openSnackbar("Sub-period (week) added successfully");
    };

    const updateSubPeriod = async (periodId: string, subPeriodId: string) => {
        if (!editSubStart || !editSubEnd) {
            openSnackbar("Please select both start and end dates");
            return;
        }

        if (editSubStart > editSubEnd) {
            openSnackbar("Start date must be before end date");
            return;
        }

        const updated = billingPeriods.map((period) => {
            if (period.id === periodId) {
                return {
                    ...period,
                    sub_periods: period.sub_periods.map((sub: any) =>
                        sub.id === subPeriodId
                            ? {
                                  ...sub,
                                  start_date: moment(editSubStart).format("YYYY-MM-DD"),
                                  end_date: moment(editSubEnd).format("YYYY-MM-DD"),
                              }
                            : sub
                    ),
                };
            }
            return period;
        });

        setBillingPeriods(updated);
        // Update DB: replace all periods for this project
        const dbPeriods = updated.flatMap((p) => [
            { week_no: 0, period_st_dt: p.start_date, period_end_dt: p.end_date },
            ...(p.sub_periods || []).map((s) => ({
                week_no: s.week_number,
                period_st_dt: s.start_date,
                period_end_dt: s.end_date,
            })),
        ]);
        console.log("Updating sub-period in DB", {
            uuid: props.profile.uuid,
            pid: props.profile.pid,
            dbPeriods
        });
        await saveProjectPeriods(props.profile.uuid, props.profile.pid, dbPeriods);
        setEditingSubPeriodId(null);
        openSnackbar("Sub-period updated successfully");
    };

    const deleteSubPeriod = async (periodId: string, subPeriodId: string) => {
        const updated = billingPeriods.map((period) => {
            if (period.id === periodId) {
                return {
                    ...period,
                    sub_periods: period.sub_periods.filter((sub: any) => sub.id !== subPeriodId),
                };
            }
            return period;
        });

        setBillingPeriods(updated);
        // Update DB: replace all periods for this project
        const dbPeriods = updated.flatMap((p) => [
            { week_no: 0, period_st_dt: p.start_date, period_end_dt: p.end_date },
            ...(p.sub_periods || []).map((s) => ({
                week_no: s.week_number,
                period_st_dt: s.start_date,
                period_end_dt: s.end_date,
            })),
        ]);
        console.log("Deleting sub-period in DB", {
            uuid: props.profile.uuid,
            pid: props.profile.pid,
            dbPeriods
        });
        await saveProjectPeriods(props.profile.uuid, props.profile.pid, dbPeriods);
        openSnackbar("Sub-period deleted successfully");
    };

    if (!hasCustomizedProject) {
        return (
            <div className="px-4 py-5 bg-white shadow sm:rounded-lg sm:p-6">
                <div className="text-center">
                    <p className="text-gray-600">
                        This tab is only available for projects with <strong>Customized</strong> billing period.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Project Start/End Dates */}
            {/* {projectDates && (
                <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-md mb-2">
                    <span className="font-semibold text-blue-800">Project Start:</span> {projectDates.start_dt}
                    <span className="mx-2 text-blue-600">|</span>
                    <span className="font-semibold text-blue-800">Project End:</span> {projectDates.end_dt}
                </div>
            )} */}
            {/* Add New Period */}
            <div className="px-4 py-5 bg-white shadow sm:rounded-lg sm:p-6">
                <div className="md:grid md:grid-cols-3 md:gap-6">
                    <div className="md:col-span-1">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Add Billing Period</h3>
                        <p className="mt-1 text-sm text-gray-500">Define custom billing periods with weekly breakdowns</p>
                    </div>
                    <div className="mt-5 md:mt-0 md:col-span-2">
                        <div className="grid grid-cols-6 gap-6">
                            <div className="col-span-6 sm:col-span-3">
                                <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                                    Period Start Date
                                </label>
                                <DatePicker
                                    selected={newPeriodStart}
                                    onChange={(date) => setNewPeriodStart(date)}
                                    dateFormat="yyyy-MM-dd"
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholderText="Select start date"
                                />
                            </div>
                            <div className="col-span-6 sm:col-span-3">
                                <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                                    Period End Date
                                </label>
                                <DatePicker
                                    selected={newPeriodEnd}
                                    onChange={(date) => setNewPeriodEnd(date)}
                                    dateFormat="yyyy-MM-dd"
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholderText="Select end date"
                                />
                            </div>
                            <div className="col-span-6 sm:col-span-6 flex items-end">
                                <button
                                    type="button"
                                    onClick={addPeriod}
                                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Add Period
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* List of Periods */}
            <div className="px-4 py-5 bg-white shadow sm:rounded-lg sm:p-6">
                <div className="md:grid md:grid-cols-3 md:gap-6">
                    <div className="md:col-span-1">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Billing Periods & Weeks</h3>
                        <p className="mt-1 text-sm text-gray-500">Manage periods and their weekly breakdowns</p>
                    </div>
                    <div className="mt-5 md:mt-0 md:col-span-2">
                        {billingPeriods.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No custom billing periods configured yet.</p>
                        ) : (
                            <div className="space-y-4">
                                {billingPeriods.map((period) => (
                                    <div key={period.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                        {/* Period Header */}
                                        <div className="bg-gray-50 px-4 py-4 flex items-center justify-between">
                                            <div className="flex-1">
                                                {editingPeriodId === period.id ? (
                                                    <div className="flex gap-2">
                                                        <DatePicker
                                                            selected={editStart}
                                                            onChange={(date) => setEditStart(date)}
                                                            dateFormat="yyyy-MM-dd"
                                                            className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                                                        />
                                                        <span className="text-gray-500 px-2 py-1">to</span>
                                                        <DatePicker
                                                            selected={editEnd}
                                                            onChange={(date) => setEditEnd(date)}
                                                            dateFormat="yyyy-MM-dd"
                                                            className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {moment(period.start_date).format("YYYY-MM-DD")} to {moment(period.end_date).format("YYYY-MM-DD")}
                                                        <span className="ml-2 text-xs text-gray-500">
                                                            ({Math.ceil(
    (moment(period.end_date).diff(moment(period.start_date), 'days') + 1) / 7
  )} weeks)
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex gap-2 ml-4">
                                                {editingPeriodId === period.id ? (
                                                    <>
                                                        <button
                                                            onClick={() => updatePeriod(period.id)}
                                                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingPeriodId(null)}
                                                            className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => {
                                                                setEditingPeriodId(period.id);
                                                                setEditStart(new Date(period.start_date));
                                                                setEditEnd(new Date(period.end_date));
                                                            }}
                                                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                                                        >
                                                            {/* Edit */}
                                                        </button>
                                                        <button
                                                            onClick={() => deletePeriod(period.id)}
                                                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                                                        >
                                                            {/* Delete */}
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                setExpandedPeriodId(
                                                                    expandedPeriodId === period.id ? null : period.id
                                                                )
                                                            }
                                                            className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                                                        >
                                                            {expandedPeriodId === period.id ? "▼" : "▶"}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Sub-periods (Weeks) */}
                                        {expandedPeriodId === period.id && (
                                            <div className="border-t border-gray-200 px-4 py-4 bg-white">
                                                {/* Add Sub-period Form */}
                                                <div className="mb-4 pb-4 border-b border-gray-200">
                                                    <h4 className="text-sm font-medium text-gray-900 mb-3">Add Weekly Period</h4>
                                                    <div className="grid grid-cols-6 gap-3">
                                                        <div className="col-span-6 sm:col-span-2">
                                                            <DatePicker
                                                                selected={newSubPeriodStart}
                                                                onChange={(date) => setNewSubPeriodStart(date)}
                                                                dateFormat="yyyy-MM-dd"
                                                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                                                placeholderText="Week start"
                                                            />
                                                        </div>
                                                        <div className="col-span-6 sm:col-span-2">
                                                            <DatePicker
                                                                selected={newSubPeriodEnd}
                                                                onChange={(date) => setNewSubPeriodEnd(date)}
                                                                dateFormat="yyyy-MM-dd"
                                                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                                                placeholderText="Week end"
                                                            />
                                                        </div>
                                                        <div className="col-span-6 sm:col-span-2">
                                                            <button
                                                                onClick={() => addSubPeriod(period.id)}
                                                                className="w-full px-3 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700"
                                                            >
                                                                Add Week
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Sub-periods List */}
                                                {period.sub_periods && period.sub_periods.length > 0 ? (
                                                    <div className="overflow-x-auto">
                                                        <table className="min-w-full text-sm">
                                                            <thead className="bg-gray-100 border-b border-gray-200">
                                                                <tr>
                                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                                                                        Week
                                                                    </th>
                                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                                                                        Start Date
                                                                    </th>
                                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                                                                        End Date
                                                                    </th>
                                                                    <th className="px-3 py-2 text-left font-medium text-gray-700">
                                                                        Actions
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-200">
                                                                {period.sub_periods.map((sub: any) => (
                                                                    <tr key={sub.id} className="hover:bg-gray-50">
                                                                        <td className="px-3 py-2 text-gray-900">
                                                                            Week {sub.week_number}
                                                                        </td>
                                                                        <td className="px-3 py-2 text-gray-900">
                                                                            {editingSubPeriodId === sub.id ? (
                                                                                <DatePicker
                                                                                    selected={editSubStart}
                                                                                    onChange={(date) => setEditSubStart(date)}
                                                                                    dateFormat="yyyy-MM-dd"
                                                                                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                                                                                />
                                                                            ) : (
                                                                                moment(sub.start_date).format("YYYY-MM-DD")
                                                                            )}
                                                                        </td>
                                                                        <td className="px-3 py-2 text-gray-900">
                                                                            {editingSubPeriodId === sub.id ? (
                                                                                <DatePicker
                                                                                    selected={editSubEnd}
                                                                                    onChange={(date) => setEditSubEnd(date)}
                                                                                    dateFormat="yyyy-MM-dd"
                                                                                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                                                                                />
                                                                            ) : (
                                                                                moment(sub.end_date).format("YYYY-MM-DD")
                                                                            )}
                                                                        </td>
                                                                        <td className="px-3 py-2 text-sm font-medium space-x-2">
                                                                            {editingSubPeriodId === sub.id ? (
                                                                                <>
                                                                                    <button
                                                                                        onClick={() =>
                                                                                            updateSubPeriod(period.id, sub.id)
                                                                                        }
                                                                                        className="text-indigo-600 hover:text-indigo-900"
                                                                                    >
                                                                                        Save
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => setEditingSubPeriodId(null)}
                                                                                        className="text-gray-600 hover:text-gray-900"
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setEditingSubPeriodId(sub.id);
                                                                                            setEditSubStart(
                                                                                                new Date(sub.start_date)
                                                                                            );
                                                                                            setEditSubEnd(
                                                                                                new Date(sub.end_date)
                                                                                            );
                                                                                        }}
                                                                                        className="text-indigo-600 hover:text-indigo-900"
                                                                                    >
                                                                                        {/* Edit */}
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() =>
                                                                                            deleteSubPeriod(period.id, sub.id)
                                                                                        }
                                                                                        className="text-red-600 hover:text-red-900"
                                                                                    >
                                                                                        {/* Delete */}
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500 text-center py-4">
                                                        No weekly periods added yet.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
